/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unused-prop-types */
import React, { ChangeEvent, useState } from 'react';
import { GetQuestionnaire } from '../../../../services/modules/questionnaire/types';
import { dateToPTBR } from '../../../../utils/formatt';
import Checkbox from '../../../Checkbox';
import DatePickerUnform from '../../../DatePickerUnform';
import Helper from '../../../Helper';
import Input from '../../../Input';
import InputRadio from '../../../InputRadio';
import InputTag from '../../../InputTag';
import SelectUnform from '../../../SelectUnform';
import {
  CharLimit,
  CheckboxGroupWrapper,
  FormGroup,
  FormGroupLabel,
} from './styles';
import { Helper as H } from './helper';
import { RangeError } from './RangeError';

type QuestionaireData = GetQuestionnaire['return']['questions'];
type Options = NonNullable<QuestionaireData[0]['options']>;

interface Common {
  tooltip: string;
  dispatchManualError?: (error: string) => void;
  onChangeFireCallback?: (value?: unknown) => void;
}

type RadioGroup = {
  label: string;
  options: Options;
  name: string;
  idQuestion: number;
} & Common;

type CheckboxGroup = {
  label: string;
  options: Options;
  name: string;
  idQuestion: number;
} & Common;

type NumberInputProps = {
  idQuestion: number;
  label: string;
  name: string;
  precision: number;
  min: number;
  max: number;
} & Common;

type DatepickerProps = {
  idQuestion: number;
  label: string;
  name: string;
} & Common;

type TextAreaProps = {
  idQuestion: number;
  label: string;
  name: string;
  characterLimit: number;
} & Common;

type TextProps = {
  idQuestion: number;
  label: string;
  name: string;
} & Common;

type ListProps = {
  idQuestion: number;
  label: string;
  name: string;
  options: Options;
} & Common;

type TagsProps = {
  idQuestion: number;
  label: string;
  name: string;
} & Common;

const RadioGroup = (props: RadioGroup): React.ReactNode => {
  const { idQuestion, label, name, options, tooltip, onChangeFireCallback } =
    props;
  return (
    <FormGroup data-question-id={idQuestion}>
      <FormGroupLabel>
        {label}
        {tooltip && <Helper message={tooltip} />}
      </FormGroupLabel>
      <InputRadio
        name={name}
        options={options.map((option) => ({
          id: String(option.idOption),
          value: String(option.idOption),
          option: String(option.option),
        }))}
        onChangeFireCallback={onChangeFireCallback}
      />
    </FormGroup>
  );
};

const CheckboxGroup = (props: CheckboxGroup): React.ReactNode => {
  const { idQuestion, label, name, options, tooltip, onChangeFireCallback } =
    props;

  const handleChange = () => {
    onChangeFireCallback?.();
  };

  return (
    <FormGroup data-question-id={idQuestion}>
      <FormGroupLabel>
        {label}
        {tooltip && <Helper message={tooltip} />}
      </FormGroupLabel>
      <CheckboxGroupWrapper>
        {options.map((option) => (
          <Checkbox
            key={option.idOption}
            name={`${name}[${option.idOption}]`}
            title={option.option}
            defaultChecked={false}
            onChange={handleChange}
          />
        ))}
      </CheckboxGroupWrapper>
    </FormGroup>
  );
};

const NumberInput = (props: NumberInputProps): React.ReactNode => {
  const {
    idQuestion,
    label,
    name,
    max,
    min,
    precision,
    tooltip,
    dispatchManualError,
    onChangeFireCallback,
  } = props;

  const interceptOnChange = (
    ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    try {
      const { formatted, raw } = H.inputNumberChange({ precision })(ev);

      if (raw < min || raw > max) {
        throw new RangeError(
          `Valor inválido. Limites: de ${min} a ${max}.`,
          formatted,
          min,
          max,
        );
      }

      dispatchManualError?.('');
      return formatted;
    } catch (error) {
      if (error instanceof RangeError) {
        dispatchManualError?.(error.message);
        return error.value;
      }
      dispatchManualError?.(error.message);
      return ev.target.value;
    } finally {
      setTimeout(() => {
        onChangeFireCallback?.();
      }, 0);
    }
  };
  return (
    <FormGroup data-question-id={idQuestion}>
      <FormGroupLabel>
        {label}
        {tooltip && <Helper message={tooltip} />}
      </FormGroupLabel>
      <Input name={name} interceptOnChange={interceptOnChange} formLook />
    </FormGroup>
  );
};

const Datepicker = (props: DatepickerProps): React.ReactNode => {
  const { idQuestion, label, name, tooltip, onChangeFireCallback } = props;

  const onChangeDate = (newValue: Date | null | undefined) => {
    onChangeFireCallback?.(newValue ? dateToPTBR(newValue) : '');
  };
  return (
    <FormGroup data-question-id={idQuestion}>
      <FormGroupLabel>
        {label}
        {tooltip && <Helper message={tooltip} />}
      </FormGroupLabel>
      <DatePickerUnform name={name} formLook icon onChangeDate={onChangeDate} />
    </FormGroup>
  );
};

const TextArea = (props: TextAreaProps): React.ReactNode => {
  const {
    idQuestion,
    label,
    name,
    characterLimit,
    tooltip,
    dispatchManualError,
    onChangeFireCallback,
  } = props;

  const [typedCount, setTypedCount] = useState(0);
  const interceptOnChange = (
    ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = ev.target;
    const remains = characterLimit - value.length;
    if (remains < 0) {
      const errorMessage = `Você excedeu o limite de caracteres permitidos (${characterLimit})`;
      dispatchManualError?.(errorMessage);
    }
    setTypedCount(value.length);
    onChangeFireCallback?.(value);
    return value;
  };
  return (
    <FormGroup data-question-id={idQuestion}>
      <FormGroupLabel>
        {label}
        {tooltip && <Helper message={tooltip} />}
      </FormGroupLabel>
      <Input
        name={name}
        type="textarea"
        maxLength={characterLimit}
        interceptOnChange={interceptOnChange}
      />
      <CharLimit>
        {typedCount}/{characterLimit}
      </CharLimit>
    </FormGroup>
  );
};

const Text = (props: TextProps): React.ReactNode => {
  const { idQuestion, label, name, tooltip, onChangeFireCallback } = props;

  const interceptOnChange = (
    ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = ev.target;
    onChangeFireCallback?.(value);
    return value;
  };

  return (
    <FormGroup data-question-id={idQuestion}>
      <FormGroupLabel>
        {label}
        {tooltip && <Helper message={tooltip} />}
      </FormGroupLabel>
      <Input name={name} formLook interceptOnChange={interceptOnChange} />
    </FormGroup>
  );
};

const List = (props: ListProps): React.ReactNode => {
  const { idQuestion, label, name, options, tooltip, onChangeFireCallback } =
    props;
  return (
    <FormGroup data-question-id={idQuestion}>
      <FormGroupLabel>
        {label}
        {tooltip && <Helper message={tooltip} />}
      </FormGroupLabel>
      <SelectUnform
        name={name}
        formLook
        options={options.map((option) => ({
          id: String(option.idOption),
          name: String(option.option),
        }))}
        callbackOnChange={onChangeFireCallback}
      />
    </FormGroup>
  );
};

const Tags = (props: TagsProps): React.ReactNode => {
  const { idQuestion, label, name, tooltip, onChangeFireCallback } = props;
  return (
    <FormGroup data-question-id={idQuestion}>
      <FormGroupLabel>
        {label}
        {tooltip && <Helper message={tooltip} />}
      </FormGroupLabel>
      <InputTag
        name={name}
        formLook
        onChangeFireCallback={onChangeFireCallback}
      />
    </FormGroup>
  );
};

const UI = {
  RadioGroup,
  CheckboxGroup,
  NumberInput,
  Datepicker,
  TextArea,
  Text,
  List,
  Tags,
};

export default UI;
